import { tx } from "@/lib/txnI18n";

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr, isInternal } from "@/lib/api";
import { DateInput } from "@/components/DateInput";
import { fl, trName } from "@/lib/finI18n";
import { coaName } from "@/lib/masterI18n";
import { ComboBox } from "@/components/ComboBox";
import { GLView, TBView, PLView, BSView, CFView, RegisterView, EquityView, TaxRecapView, SPTView } from "@/components/reports/FinViews";
import { ARAPView } from "@/components/reports/ARAPView";
import { CoretaxView } from "@/components/reports/CoretaxView";
import { LKPMView } from "@/components/reports/LKPMView";
import { RoiView } from "@/components/reports/RoiView";
import { BankCashView } from "@/components/reports/BankCashView";
import { SubledgerView } from "@/components/reports/SubledgerView";
import { UnifikasiMasa } from "@/components/reports/UnifikasiMasa";
import { DrillProvider } from "@/components/reports/Drill";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileText, FileType2, FileCode, Search, X, ListTree, MousePointerClick } from "lucide-react";

const FILTERABLE = ["general-ledger", "trial-balance", "profit-loss", "balance-sheet", "cash-flow", "sales-register", "purchase-register"];
const MODED = ["general-ledger", "trial-balance", "profit-loss", "balance-sheet", "cash-flow", "ppn-recap", "pph-recap", "ar", "ap", "coretax-l1"];
const EXPANDABLE = ["general-ledger", "trial-balance", "profit-loss", "balance-sheet", "ar", "ap", "coretax-l1"];
const LAYOUT = ["general-ledger", "trial-balance", "profit-loss", "balance-sheet", "cash-flow", "ar", "ap", "coretax-l1"];
const SEARCH_ONLY = ["ppn-recap", "pph-recap", "subledger"];
const LEDGERED = ["general-ledger", "subledger"];
const OPENABLE = ["general-ledger", "trial-balance", "profit-loss", "balance-sheet"];
const CORETAX = "coretax-l1";
const TABS = [["general-ledger", "tab-gl", "reports.tab.gl"], ["subledger", "tab-subledger", "reports.tab.sub"], ["trial-balance", "tab-tb", "reports.tab.tb"], ["profit-loss", "tab-pl", "reports.tab.pl"],
  ["balance-sheet", "tab-bs", "reports.tab.bs"], ["equity-changes", "tab-eq", "reports.tab.equity"], ["cash-flow", "tab-cf", "reports.tab.cf"], ["bank-cash", "tab-bank", "reports.tab.bank"], ["roi", "tab-roi", "reports.tab.roi"],
  ["ar", "tab-ar", "reports.tab.ar"], ["ap", "tab-ap", "reports.tab.ap"],
  ["sales-register", "tab-sr", "reports.tab.sr"], ["purchase-register", "tab-pr", "reports.tab.pr"], ["ppn-recap", "tab-ppn", "reports.tab.ppn"],
  ["pph-recap", "tab-pph", "reports.tab.pph"], ["spt-unifikasi", "tab-spt", "reports.tab.spt"], [CORETAX, "tab-coretax", "reports.tab.coretax"],
  ["lkpm", "tab-lkpm", "reports.tab.lkpm"]];
const CLIENT_TABS = ["general-ledger", "subledger", "trial-balance", "balance-sheet", "bank-cash"];
const lkMonths = (type, no) => (type === "s" ? [(no - 1) * 6 + 1, no * 6] : [(no - 1) * 3 + 1, no * 3]);

function ExportButtons({ tab, exportFile }) {
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="export-xlsx"><Download className="h-4 w-4 mr-1" />Excel</Button>
      <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="export-pdf"><FileType2 className="h-4 w-4 mr-1" />PDF</Button>
      <Button variant="outline" size="sm" onClick={() => exportFile("csv")} data-testid="export-csv"><FileText className="h-4 w-4 mr-1" />CSV</Button>
      {tab === "spt-unifikasi" && <Button variant="outline" size="sm" onClick={() => exportFile("xml")} data-testid="export-xml"><FileCode className="h-4 w-4 mr-1" />XML</Button>}
    </>
  );
}

function OptionsBar({ tab, lang, t, mode, setMode, expand, setExpand, layout, setLayout, withOpen, setWithOpen }) {
  return (
    <>
      {MODED.includes(tab) && (
        <div className="flex rounded-md border border-border overflow-hidden" data-testid="report-mode-toggle">
          {["summary", "detail"].map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`report-mode-${m}`}
              className={`px-3 h-9 text-xs font-medium transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{fl(m, lang)}</button>
          ))}
        </div>
      )}
      {EXPANDABLE.includes(tab) && (
        <button type="button" onClick={() => setExpand(!expand)} data-testid="report-expand-toggle" aria-pressed={expand}
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-medium transition-colors ${expand ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
          <ListTree className="h-3.5 w-3.5" />{fl("expandAll", lang)}
        </button>
      )}
      {OPENABLE.includes(tab) && (
        <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-border text-xs font-medium cursor-pointer" data-testid="report-open-toggle">
          <input type="checkbox" checked={withOpen} onChange={(e) => setWithOpen(e.target.checked)} className="accent-primary" data-testid="report-open-checkbox" />
          {fl("openInExport", lang)}
        </label>
      )}
      {LAYOUT.includes(tab) && (
        <Select value={layout} onValueChange={setLayout}>
          <SelectTrigger className="w-36 h-9" data-testid="report-layout" title={fl("layoutLbl", lang)}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="horizontal">{t("rep.horizontal")}</SelectItem>
            <SelectItem value="vertical">{t("rep.vertical")}</SelectItem>
          </SelectContent>
        </Select>
      )}
    </>
  );
}

export default function Reports() {
  const { user, selectedClientId } = useAuth();
  const { t, lang } = useI18n();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;
  const [tab, setTab] = useState("trial-balance");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [coaSel, setCoaSel] = useState("");
  const [ledSel, setLedSel] = useState("");
  const [ledgers, setLedgers] = useState([]);
  const [mode, setMode] = useState("detail");
  const [expand, setExpand] = useState(false);
  const [withOpen, setWithOpen] = useState(true);
  const [scope, setScope] = useState("purchase");
  const [layout, setLayout] = useState("horizontal");
  const [ctYear, setCtYear] = useState(new Date().getFullYear());
  const [ctSector, setCtSector] = useState("");
  const [lkType, setLkType] = useState("q");
  const [lkNo, setLkNo] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [lkYear, setLkYear] = useState(new Date().getFullYear());
  const [roiYear, setRoiYear] = useState(new Date().getFullYear());
  const [coaList, setCoaList] = useState([]);
  const [report, setReport] = useState(null);
  const reqId = useRef(0);
  const isCt = tab === CORETAX;
  const isLk = tab === "lkpm";
  const isRoi = tab === "roi";
  const tabs = isInternal(user.role) ? TABS : TABS.filter(([v]) => CLIENT_TABS.includes(v));

  useEffect(() => { const h = setTimeout(() => setQDeb(q), 350); return () => clearTimeout(h); }, [q]);
  useEffect(() => { if (clientId) api.get(`/coa?client_id=${clientId}`).then(({ data }) => setCoaList(data)).catch(() => {}); }, [clientId]);
  useEffect(() => {
    setLedSel("");
    if (clientId) api.get(`/ledgers?client_id=${clientId}&limit=2000`).then(({ data }) => setLedgers(data)).catch(() => {});
  }, [clientId]);

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams({ client_id: clientId, lang });
    if (isCt) {
      p.set("year", ctYear);
      if (ctSector) p.set("sector", ctSector);
    } else if (isLk) {
      const [mf, mt] = lkMonths(lkType, lkNo);
      p.set("year", lkYear); p.set("month_from", mf); p.set("month_to", mt);
    } else if (isRoi) {
      p.set("year", roiYear);
    } else {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    }
    if (FILTERABLE.includes(tab) || SEARCH_ONLY.includes(tab)) {
      if (qDeb) p.set("q", qDeb);
      if (coaSel && FILTERABLE.includes(tab)) p.set("coa", coaSel);
    }
    if (LEDGERED.includes(tab) && ledSel) p.set("ledger", ledSel);
    if (tab === "purchase-register") p.set("scope", scope);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [clientId, lang, dateFrom, dateTo, qDeb, coaSel, ledSel, tab, scope, isCt, ctYear, ctSector, isLk, lkType, lkNo, lkYear, isRoi, roiYear]);

  const load = useCallback(async () => {
    if (!clientId) return;
    const id = ++reqId.current;
    setReport(null);
    try {
      const { data } = await api.get(`/reports/${tab}?${qs()}`);
      if (id === reqId.current) setReport({ tab, data });
    } catch (e) { if (id === reqId.current) toast.error(apiErr(e.response?.data?.detail)); }
  }, [clientId, tab, qs]);
  useEffect(() => { load(); }, [load]);
  const reloadSilent = useCallback(async () => {
    try { const { data } = await api.get(`/reports/${tab}?${qs()}`); setReport({ tab, data }); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  }, [tab, qs]);

  const exportFile = async (fmt, extra = {}, suffix = "") => {
    try {
      const opts = { mode, expand: expand ? 1 : 0, layout, opening: withOpen ? 1 : 0, ...extra };
      const res = await api.get(`/reports/export?report=${tab}&fmt=${fmt}&${qs(opts)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const period = isCt ? `_${ctYear}` : isLk ? `_${lkYear}_${lkType.toUpperCase()}${lkNo}` : `${dateFrom ? `_${dateFrom}` : ""}${dateTo ? `_${dateTo}` : ""}`;
      a.href = url; a.download = `${tab}${suffix}${period}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${tx("exportOk", lang)} (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Gagal export"); }
  };

  const syncInvoices = async () => {
    if (!window.confirm(fl("syncConfirm", lang))) return;
    try {
      const { data } = await api.post(`/invoices/sync-journals?client_id=${clientId}`);
      toast.success(`${data.posted} invoice diposting`); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const drillBase = useMemo(() => (isCt ? { client_id: clientId, lang, date_from: `${ctYear}-01-01`, date_to: `${ctYear}-12-31` }
    : { client_id: clientId, lang, date_from: dateFrom, date_to: dateTo }), [isCt, clientId, lang, ctYear, dateFrom, dateTo]);
  const data = report?.tab === tab ? report.data : null;
  const coaOpts = coaList.map((c) => ({ value: c.id, label: `${c.code} · ${coaName(c, lang)}` }));
  const searchable = FILTERABLE.includes(tab) || SEARCH_ONLY.includes(tab);
  const ledOpts = [{ value: "", label: fl("allLedger", lang) }, ...ledgers.map((l) => ({ value: l.name, label: `${trName(l, lang)}${l.npwp || l.nik ? ` · ${l.npwp || l.nik}` : ""}` }))];

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="page-sticky flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("reports.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("reports.desc")}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          {!isCt && !isLk && !isRoi && <div className="space-y-1"><label className="text-xs text-muted-foreground">{t("inv.from")}</label><DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-9" data-testid="report-date-from" /></div>}
          {!isCt && !isLk && !isRoi && <div className="space-y-1"><label className="text-xs text-muted-foreground">{t("inv.to")}</label><DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-9" data-testid="report-date-to" /></div>}
          {!isRoi && tab !== "bank-cash" && <ExportButtons tab={tab} exportFile={exportFile} />}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map(([v, id, k]) => <TabsTrigger key={v} value={v} data-testid={id}>{t(k)}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {(searchable || MODED.includes(tab) || LAYOUT.includes(tab) || tab === "purchase-register") && (
        <Card className="p-3 flex flex-wrap items-center gap-2" data-testid="report-filter-bar">
          {searchable && (
            <>
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={fl("search", lang)} className="pl-9 h-9" data-testid="report-search" />
              </div>
              {FILTERABLE.includes(tab) && <div className="w-72"><ComboBox testid="report-coa-filter" value={coaSel} onChange={setCoaSel} placeholder={fl("allCoa", lang)} searchPlaceholder={fl("searchCoa", lang)} options={coaOpts} /></div>}
              {LEDGERED.includes(tab) && <div className="w-72"><ComboBox testid="report-ledger-filter" value={ledSel} onChange={setLedSel} placeholder={fl("allLedger", lang)} searchPlaceholder={fl("searchLedger", lang)} options={ledOpts} /></div>}
              {(q || coaSel || ledSel) && <Button variant="ghost" size="sm" onClick={() => { setQ(""); setCoaSel(""); setLedSel(""); }} data-testid="report-filter-clear"><X className="h-4 w-4 mr-1" />Reset</Button>}
            </>
          )}
          <OptionsBar tab={tab} lang={lang} t={t} mode={mode} setMode={setMode} expand={expand} setExpand={setExpand} layout={layout} setLayout={setLayout} withOpen={withOpen} setWithOpen={setWithOpen} />
          {tab === "purchase-register" && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-56 h-9" data-testid="purchase-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase">{fl("scopePurchase", lang)}</SelectItem>
                <SelectItem value="all">{fl("scopeAll", lang)}</SelectItem>
              </SelectContent>
            </Select>
          )}
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="report-drill-hint"><MousePointerClick className="h-3.5 w-3.5" />{fl("drillHint", lang)}</span>
        </Card>
      )}

      <DrillProvider base={drillBase} lang={lang}>
        {!data && !isLk && <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="report-loading">{fl("loading", lang)}</Card>}
        {data && tab === "general-ledger" && <GLView report={data} mode={mode} lang={lang} onExportAccount={(a) => exportFile("xlsx", { coa: a.account_id }, `_${a.account_code}`)} />}
        {data && tab === "subledger" && <SubledgerView report={data} lang={lang} onPick={setLedSel} onBack={() => setLedSel("")} />}
        {data && tab === "trial-balance" && <TBView report={data} mode={mode} lang={lang} />}
        {data && tab === "profit-loss" && <PLView report={data} mode={mode} lang={lang} layout={layout} />}
        {data && tab === "balance-sheet" && <BSView report={data} mode={mode} lang={lang} layout={layout} />}
        {data && tab === "equity-changes" && <EquityView report={data} lang={lang} />}
        {data && tab === "cash-flow" && <CFView report={data} mode={mode} lang={lang} />}
        {data && tab === "bank-cash" && <BankCashView report={data} clientId={clientId} canEdit={isInternal(user.role)} onReload={reloadSilent} />}
        {data && isRoi && <RoiView report={data} lang={lang} year={roiYear} onYear={setRoiYear} />}
        {data && (tab === "ar" || tab === "ap") && <ARAPView report={data} mode={mode} expand={expand} lang={lang} kind={tab} />}
        {data && tab === "sales-register" && <RegisterView report={data} sales lang={lang} onSync={isInternal(user.role) ? syncInvoices : null} />}
        {data && tab === "purchase-register" && <RegisterView report={data} lang={lang} />}
        {data && tab === "ppn-recap" && <TaxRecapView report={data} mode={mode} lang={lang} kind="ppn" />}
        {data && tab === "pph-recap" && <TaxRecapView report={data} mode={mode} lang={lang} kind="pph" />}
        {data && tab === "spt-unifikasi" && <SPTView report={data} lang={lang} />}
        {data && tab === "spt-unifikasi" && <UnifikasiMasa clientId={clientId} year={(dateTo || dateFrom || new Date().toISOString()).slice(0, 4)} lang={lang} />}
        {isLk && <LKPMView report={data} lang={lang} ptype={lkType} pno={lkNo} year={lkYear} onYear={setLkYear} onNo={setLkNo}
          onType={(v) => { setLkType(v); if (v === "s" && lkNo > 2) setLkNo(1); }} />}
        {data && isCt && <CoretaxView report={data} mode={mode} lang={lang} clientId={clientId} year={ctYear} onYear={setCtYear}
          onSector={async (s) => {
            setCtSector(s);
            if (isInternal(user.role)) { try { await api.put("/coretax/settings", { client_id: clientId, sector: s }); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } }
          }}
          onReload={load} canEdit={isInternal(user.role)} />}
      </DrillProvider>
    </div>
  );
}
